"use client";

/**
 * Is address autocomplete available in this deployment?
 *
 * Answers `false` until proven otherwise, so a surface renders its ordinary
 * text input and only ever UPGRADES to the lookup control. That ordering is the
 * whole point: an unconfigured deployment must hide the feature, never show a
 * search box that silently returns nothing. A user cannot tell a missing API
 * key from a broken network, so we don't make them guess.
 *
 * One probe per mount, cached per page load — the answer is a deployment fact
 * and cannot change between two renders of the same form.
 */

import { useEffect, useState } from "react";

/** Module-scope memo: a deployment fact, fetched at most once per page load. */
let cachedAvailability: boolean | null = null;
let inFlightProbe: Promise<boolean> | null = null;

async function probeAvailability(): Promise<boolean> {
  if (cachedAvailability !== null) return cachedAvailability;
  if (inFlightProbe) return inFlightProbe;

  inFlightProbe = fetch("/api/address/lookup", { credentials: "include" })
    .then(async (response) => {
      if (!response.ok) return false;
      const body: unknown = await response.json();
      return Boolean(
        body && typeof body === "object" && (body as { configured?: unknown }).configured
      );
    })
    .catch(() => false)
    .then((result) => {
      cachedAvailability = result;
      inFlightProbe = null;
      return result;
    });

  return inFlightProbe;
}

/** Test-only: forget the memo so cases cannot bleed into each other. */
export function resetAddressLookupAvailabilityForTests(): void {
  cachedAvailability = null;
  inFlightProbe = null;
}

export function useAddressLookupAvailable(): boolean {
  const [available, setAvailable] = useState(cachedAvailability ?? false);

  useEffect(() => {
    let active = true;
    void probeAvailability().then((result) => {
      if (active) setAvailable(result);
    });
    return () => {
      active = false;
    };
  }, []);

  return available;
}
